import { parseISO, format } from "date-fns";

export interface PollSlotVote {
  poll_slot_id: string;
  user_id?: string | null;
  player_id?: number | null;
}

export interface PollSlotRow {
  id: string;
  poll_day_id: string;
  slot_time: string; // HH:mm:ss or HH:mm
  vote_count?: number;
}

export interface PollDayRow {
  id: string;
  day: string; // YYYY-MM-DD
  position: number;
  slots?: PollSlotRow[];
}

export interface PollSlotAggregate {
  slotId: string;
  dayId: string;
  day: string;
  hour: number;
  votes: number;
}

const SUGGESTED_THRESHOLD = 0.7;

export function hourFromSlotTime(slotTime: string): number {
  const part = slotTime.split(":")[0] ?? "0";
  return parseInt(part, 10);
}

export function formatHourLabel(hour: number): string {
  return `${hour.toString().padStart(2, "0")}:00`;
}

/** Count votes per slot from raw vote rows or pre-aggregated counts. */
export function aggregateSlotVotes(
  slots: PollSlotRow[],
  votes: PollSlotVote[] | { poll_slot_id: string; vote_count: number }[]
): Map<string, number> {
  const counts = new Map<string, number>();

  for (const slot of slots) {
    counts.set(slot.id, slot.vote_count ?? 0);
  }

  if (votes.length > 0 && "user_id" in votes[0]) {
    for (const v of votes as PollSlotVote[]) {
      counts.set(v.poll_slot_id, (counts.get(v.poll_slot_id) ?? 0) + 1);
    }
  }

  return counts;
}

/** Distinct voters across all slots in a poll. */
export function countDistinctVoters(votes: PollSlotVote[]): number {
  const keys = new Set<string>();
  for (const v of votes) {
    if (v.user_id) keys.add(`u:${v.user_id}`);
    else if (v.player_id != null) keys.add(`p:${v.player_id}`);
  }
  return keys.size;
}

/** Distinct voters who voted any slot on a given day (max one per player). */
export function dayVoteCounts(
  dayId: string,
  slots: PollSlotRow[],
  votes: PollSlotVote[]
): number {
  const slotIds = new Set(slots.filter((s) => s.poll_day_id === dayId).map((s) => s.id));
  const keys = new Set<string>();
  for (const v of votes) {
    if (!slotIds.has(v.poll_slot_id)) continue;
    if (v.user_id) keys.add(`u:${v.user_id}`);
    else if (v.player_id != null) keys.add(`p:${v.player_id}`);
  }
  return keys.size;
}

/** Distinct voters per day (only days with at least one vote are included). */
export function dayVoterCountsForDays(
  days: PollDayRow[],
  votes: PollSlotVote[]
): Map<string, number> {
  const allSlots = days.flatMap((d) => d.slots ?? []);
  const counts = new Map<string, number>();
  for (const day of days) {
    const n = dayVoteCounts(day.id, allSlots, votes);
    if (n > 0) counts.set(day.id, n);
  }
  return counts;
}

export function buildSlotAggregates(
  days: PollDayRow[],
  slotVoteCounts: Map<string, number>
): PollSlotAggregate[] {
  return days.flatMap((day) =>
    (day.slots ?? []).map((slot) => ({
      slotId: slot.id,
      dayId: day.id,
      day: day.day,
      hour: hourFromSlotTime(slot.slot_time),
      votes: slotVoteCounts.get(slot.id) ?? 0,
    }))
  );
}

export function topSlots(
  aggregates: PollSlotAggregate[],
  limit = 3
): PollSlotAggregate[] {
  return [...aggregates]
    .sort((a, b) => b.votes - a.votes || a.hour - b.hour)
    .slice(0, limit);
}

export function suggestedSlots(
  aggregates: PollSlotAggregate[],
  totalVoters: number,
  threshold = SUGGESTED_THRESHOLD
): Set<string> {
  if (totalVoters <= 0) return new Set();
  const minVotes = Math.ceil(totalVoters * threshold);
  return new Set(
    aggregates.filter((s) => s.votes >= minVotes).map((s) => s.slotId)
  );
}

export function composeEventDate(day: string, slotTime: string): string {
  const hour = hourFromSlotTime(slotTime);
  const minute = parseInt(slotTime.split(":")[1] ?? "0", 10);
  const d = parseISO(`${day}T12:00:00`);
  d.setHours(hour, minute, 0, 0);
  return d.toISOString();
}

export function surveyDayGridCols(count: number): string {
  if (count <= 1) return "grid-cols-1";
  if (count === 2) return "grid-cols-2";
  return "grid-cols-3";
}

export function getInitialAdminConfirm(
  aggregates: PollSlotAggregate[]
): { dayId: string; slotId: string; hour: number } | null {
  const ranked = topSlots(aggregates, 1)[0];
  if (ranked) {
    return { dayId: ranked.dayId, slotId: ranked.slotId, hour: ranked.hour };
  }
  return null;
}

export function formatPollCountdown(ms: number): string {
  if (ms <= 0) return "00:00:00";
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const time = [hours, minutes, seconds]
    .map((n) => n.toString().padStart(2, "0"))
    .join(":");
  return days > 0 ? `${days}d ${time}` : time;
}

export function dayKeyFromDate(date: Date): string {
  return format(date, "yyyy-MM-dd");
}
